import { useId, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { twMerge } from "tailwind-merge";

import { useDialogFocus } from "./ui/useDialogFocus";
import { useEscapeDelDialogo } from "./ui/useEscapeDelDialogo";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Título arriba del modal. */
  title: string;
  /** Subtítulo opcional debajo del título. */
  subtitle?: string | undefined;
  /** Icono opcional al lado del título. */
  icon?: LucideIcon | undefined;
  /** Ancho del modal. Default md (480px). */
  size?: "sm" | "md" | "lg" | "xl" | undefined;
  /** Cuerpo scrolleable. */
  children: React.ReactNode;
  /** Acciones al pie en una barra sticky. */
  footer?: React.ReactNode | undefined;
  /** Cerrar con click en backdrop. Default false (UX-16): un clic fuera
   *  no debe borrar un formulario a medio llenar. Pasarlo en true sólo en
   *  diálogos de lectura. Esc y la X siempre cierran. */
  closeOnBackdrop?: boolean | undefined;
}

const SIZE_CLS: Record<NonNullable<ModalProps["size"]>, string> = {
  sm: "max-w-[400px]",
  md: "max-w-[520px]",
  lg: "max-w-[680px]",
  xl: "max-w-[880px]",
};

/**
 * Modal centrado en pantalla con backdrop blur. Patrón estándar para
 * formularios que necesitan foco completo del usuario (alta de socio,
 * sector, tarifa, etc.). El backdrop opaca el resto de la UI y la card
 * sale al centro con una entrada suave de 180ms.
 *
 * Diferencias con `Drawer`:
 * - Drawer: detalle/edición sin perder contexto de lista (slide-in
 *   derecha). Apto para "ver registro X" sin salir de la grilla.
 * - Modal: alta/edición que necesita foco — contexto detrás se opaca.
 *   Apto para "Nuevo socio", "Nuevo sector", confirmaciones críticas.
 *
 * Esc cierra. Click en backdrop cierra sólo con `closeOnBackdrop`.
 * Foco (A11Y-11): al abrir va al primer elemento enfocable del cuerpo (o
 * al que tenga `autoFocus`), si no hay ninguno al título; Tab queda
 * atrapado dentro y al cerrar vuelve al elemento que lo abrió.
 */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  icon: Icon,
  size = "md",
  children,
  footer,
  closeOnBackdrop = false,
}: ModalProps): JSX.Element | null {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  useDialogFocus(open, panelRef, bodyRef, titleRef);

  useEscapeDelDialogo(open, titleId, onClose);

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-40 flex items-center justify-center p-4 motion-safe:animate-[modal-fade_180ms_ease-out]"
    >
      {/* Backdrop con blur sutil — diferencia del Drawer (neutral-900/30):
          aquí va más oscuro (neutral-900/50) porque queremos foco total. */}
      <button
        type="button"
        aria-label="Cerrar"
        onClick={closeOnBackdrop ? onClose : undefined}
        tabIndex={-1}
        className="absolute inset-0 bg-neutral-900/50 backdrop-blur-sm transition-opacity duration-200"
      />

      {/* Card centrada */}
      <div
        ref={panelRef}
        className={twMerge(
          "relative w-full flex flex-col rounded-xl bg-white shadow-2xl ring-1 ring-neutral-200 max-h-[calc(100vh-2rem)]",
          "motion-safe:animate-[modal-pop_180ms_cubic-bezier(0.2,0.7,0.3,1)]",
          SIZE_CLS[size],
        )}
      >
        {/* Header */}
        <header className="flex items-start justify-between gap-3 border-b border-neutral-200 px-5 py-4">
          <div className="flex items-start gap-3 min-w-0">
            {Icon && (
              <div className="grid place-items-center h-9 w-9 rounded-md bg-brand-50 text-brand-700 shrink-0">
                <Icon className="h-4 w-4" aria-hidden="true" />
              </div>
            )}
            <div className="min-w-0 leading-tight">
              <h2
                id={titleId}
                ref={titleRef}
                tabIndex={-1}
                className="text-base font-semibold text-neutral-900 truncate focus:outline-none"
              >
                {title}
              </h2>
              {subtitle && (
                <p className="text-xs text-neutral-600 truncate mt-0.5">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar modal"
            // Superficie de 40 px (44 en pantallas táctiles) con el mismo
            // ícono; el margen negativo la mantiene alineada con el título.
            className="-mr-2 -mt-1.5 grid h-10 w-10 place-items-center rounded-md text-neutral-500 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11 hover:bg-neutral-100 hover:text-neutral-700 transition-colors shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </header>

        {/* Body scrolleable */}
        <div ref={bodyRef} className="flex-1 overflow-y-auto px-5 py-4">
          {children}
        </div>

        {/* Footer sticky con acciones */}
        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t border-neutral-200 bg-neutral-50 px-5 py-3">
            {footer}
          </footer>
        )}
      </div>

      <style>{`
        @keyframes modal-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes modal-pop {
          from { transform: scale(0.97) translateY(8px); opacity: 0 }
          to { transform: scale(1) translateY(0); opacity: 1 }
        }
      `}</style>
    </div>,
    document.body,
  );
}
